import { useEffect, useState } from 'react';
import api from '@/lib/api';
import Hero from '@/components/shop/Hero';
import HeroBanner from '@/components/shop/HeroBanner';
import SaleTimer from '@/components/shop/SaleTimer';
import CategoryCard from '@/components/shop/CategoryCard';
import ProductCard from '@/components/shop/ProductCard';
import { Link } from 'react-router-dom';
import { ArrowRight } from '@phosphor-icons/react';
import { HOME } from '@/constants/testIds';
import { useSettings } from '@/context/SettingsContext';

export default function Home() {
  const { settings } = useSettings();
  const [categories, setCategories] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [loading, setLoading] = useState(true);
  const heroCustomEnabled = !!(settings?.hero_banner?.enabled && settings?.hero_banner?.image_url);

  // Re-run whenever content_version bumps (product/category/banner/timer edited by admin)
  const contentVersion = settings?.content_version || 0;

  useEffect(() => {
    let mounted = true;
    Promise.all([
      // with_products=1 => only categories that actually have at least one active product
      api.get('/categories', { params: { with_products: 1 } }).then((r) => r.data).catch(() => []),
      // Latest 8 products (respects manual priority ASC then created_at DESC)
      api.get('/products', { params: { limit: 8 } }).then((r) => r.data).catch(() => []),
    ]).then(([cats, prods]) => {
      if (!mounted) return;
      setCategories(cats);
      setFeatured(prods);
      setLoading(false);
    });
    return () => { mounted = false; };
  }, [contentVersion]);

  return (
    <div className="fade-in space-y-8">
      {heroCustomEnabled ? (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
          <HeroBanner />
        </div>
      ) : (
        <Hero />
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SaleTimer />
      </div>

      {/* Categories */}
      <section
        id="categories"
        data-testid={HOME.categoriesSection}
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16"
      >
        <div className="flex items-end justify-between gap-4 mb-6">
          <div>
            <p className="text-xs uppercase tracking-[0.18em] text-terracotta-700 font-semibold">Shop by</p>
            <h2 className="font-heading text-3xl sm:text-4xl text-stone-900 mt-1">Categories</h2>
          </div>
          <Link to="/shop" className="hidden sm:inline-flex items-center gap-1 text-sm font-medium text-olive-800 hover:text-olive-900">
            View all <ArrowRight size={14} weight="bold" />
          </Link>
        </div>
        {loading ? (
          <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-10 gap-2">
            {Array.from({ length: 10 }).map((_, i) => (
              <div key={i} className="aspect-square rounded-2xl bg-stone-100 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-10 gap-2">
            {categories.map((c) => <CategoryCard key={c.id} category={c} />)}
          </div>
        )}
      </section>

      {/* Featured (latest 8) */}
      <section
        data-testid={HOME.featuredSection}
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 sm:pb-20"
      >
        <div className="flex items-end justify-between gap-4 mb-6">
          <div>
            <p className="text-xs uppercase tracking-[0.18em] text-terracotta-700 font-semibold">Aaj ke fresh</p>
            <h2 className="font-heading text-3xl sm:text-4xl text-stone-900 mt-1">Featured Products</h2>
          </div>
          <Link to="/shop" className="hidden sm:inline-flex items-center gap-1 text-sm font-medium text-olive-800 hover:text-olive-900">
            View all <ArrowRight size={14} weight="bold" />
          </Link>
        </div>
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="aspect-[3/4] rounded-3xl bg-stone-100 animate-pulse" />
            ))}
          </div>
        ) : featured.length === 0 ? (
          <p className="text-stone-500 text-sm">No products yet. Check back soon!</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {featured.slice(0, 8).map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        )}
      </section>

      {/* Promise band */}
      <section className="bg-olive-800 text-cream-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid sm:grid-cols-3 gap-8 text-center sm:text-left">
          <div>
            <p className="font-heading text-2xl">Fresh & Reliable</p>
            <p className="text-sm text-cream-200/80 mt-1">Quality groceries and daily essentials</p>
          </div>
          <div>
            <p className="font-heading text-2xl">Cash on Delivery</p>
            <p className="text-sm text-cream-200/80 mt-1">No app, no signup — just place your order</p>
          </div>
          <div>
            <p className="font-heading text-2xl">Faridabad & Ballabgarh</p>
            <p className="text-sm text-cream-200/80 mt-1">Serving nearby areas · INR ₹ · Expansion planned</p>
          </div>
        </div>
      </section>
    </div>
  );
}
